import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLazyQuery } from "@apollo/client";

import Link from "core/Link";
import Tooltip from "core/Tooltip";
import { Wrapper, ToolTipIcon } from "./styles";

import { GET_PORTAL_URL } from "./queries";

const makeRedirectPath = customPathname => {
    if (!customPathname && typeof window.location.toString === "function") {
        return window.location.toString();
    }
    return (
        window.location.origin +
        (customPathname || window.location.pathname + window.location.search)
    );
};

const StripePortalLink = ({
    client, // apollo-client to support legacy screens
    showTooltip = true,
    redirectPath = "",
    organizationId = null,
    errorLabel = "Failed to Load",
    children,
    ...props
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [fetchPortalLink, { data }] = useLazyQuery(GET_PORTAL_URL, {
        client // only needed for legacy pages
    });

    const onClick = async () => {
        if (isProcessing) {
            return;
        }
        setIsProcessing(true);

        fetchPortalLink({
            variables: {
                returnUrl: makeRedirectPath(redirectPath),
                organizationId
            },
            fetchPolicy: "no-cache"
        });
    };

    useEffect(() => {
        if (isProcessing && data) {
            if (
                !data.stripeCustomerPortalUrl ||
                data.stripeCustomerPortalUrl.error ||
                !data.stripeCustomerPortalUrl.url
            ) {
                setHasError(true);
            } else {
                window.location = data.stripeCustomerPortalUrl.url;
            }
            setIsProcessing(false);
        }
    }, [isProcessing, data]);

    return (
        <Wrapper>
            <Link
                {...props}
                isButton
                disableVisitedStyle
                isDisabled={isProcessing}
                onClick={onClick}
            >
                {hasError ? errorLabel : children}
            </Link>
            {showTooltip && (
                <Tooltip
                    clickToOpen
                    message={
                        <>
                            Noun Project partners with Stripe for simplified
                            billing and PCI Level 1 security. We don't store
                            payment information on our servers.{" "}
                            <a
                                href="https://stripe.com/docs/security"
                                target="_blank"
                            >
                                Learn more
                            </a>
                        </>
                    }
                    size="large"
                >
                    <ToolTipIcon />
                </Tooltip>
            )}
        </Wrapper>
    );
};

StripePortalLink.propTypes = {
    client: PropTypes.object,
    showTooltip: PropTypes.bool,
    redirectPath: PropTypes.string,
    organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    errorLabel: PropTypes.string
};

export default StripePortalLink;
